import { Task } from '@core/entities/task/Task'
import { useModal } from '@common/GlobalModal'
import { NextTaskStepModal } from '@modules/tasks/taskDetails/modals/NextTaskStepModal'
import { Button } from '@common/material'
import { localizedStrings } from '@core/strings'
import { useSubmitTaskStep } from '@core/react-query/features/tasks/task'
import { useToast } from '@core/toast'
import { statuses } from '@core/theme/statuses'

type TaskStepActionProps = {
  task: Task
}

export const TaskStepAction: React.FC<TaskStepActionProps> = ({ task }) => {
  const { showModal } = useModal()
  const toast = useToast()
  const submitTaskStepMutation = useSubmitTaskStep()

  const handleOnMoveToNextStep = () => {
    showModal(<NextTaskStepModal task={task} />)
  }

  const handleOnMoveToDone = () => {
    // If the step has questions, we need to show the modal to answer them
    if (task?.hasCurrentStepQuestions()) {
      showModal(<NextTaskStepModal task={task} />)
      return
    }

    // If the step has no questions, we can just submit the step to done
    submitTaskStepMutation.mutate(
      {
        id: task.id,
        taskTemplateId: task.template.currentVersion.templateId,
        assigneeUserId: task.assignee.id,
        stepAnswers: { answers: [] },
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.taskMovedToCompletedSuccessfully)
        },
        onError: (error) => {
          toast.error(error.message)
        },
      },
    )
  }

  if (task?.isAwaitingApproval() || task?.isCancelled()) {
    return null
  }

  if (
    task?.hasPermission('SUBMIT_STEP') &&
    ((task?.isNextStepApproval() && !task?.hasRequestedChanges()) ||
      task?.isNextStepWork())
  ) {
    return (
      <Button
        variant='contained'
        size='small'
        color='primary'
        sx={{ mr: 1.5 }}
        onClick={handleOnMoveToNextStep}
        data-test='move-to-next-step-button'
      >
        {`${localizedStrings.moveTo} ${task?.nextStep?.name}`}
      </Button>
    )
  }

  if (
    task?.hasPermission('SUBMIT_STEP') &&
    task?.isNextStepDone() &&
    !task?.isDone()
  ) {
    return (
      <Button
        variant='contained'
        size='small'
        color='primary'
        sx={{ mr: 1.5 }}
        onClick={handleOnMoveToDone}
        data-test='move-to-done-button'
      >
        {`${localizedStrings.moveTo} ${statuses.completed.label}`}
      </Button>
    )
  }

  return null
}
